import type {
  DataGridColumn,
  DataGridColumnOrderState,
  DataGridColumnPinningState,
  DataGridColumnSizingState,
  DataGridColumnVisibilityState,
  TableFilterState,
  TableSortingState,
} from '@ibsys/kit';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ConversationListItem } from '@/entities/conversations';
import {
  type SearchConversationDownloadRequest,
  type SearchDialogsPage,
  type SearchQueryRequest,
  useDownloadConversationsSearch,
} from '@/entities/search';
import type { ExpertReportSettings, TableHeaderOrientation } from '@/entities/settings';
import { readApiErrorMessage } from '@/shared/api';
import { browserTimezone, toastError } from '@/shared/lib';
import { orderColumnIdsAsInTable, orderExpertReportSettingsAsInTable } from './columnsOrder';
import { ATTRIBUTE_COLUMN_PREFIX, attributeColumnId } from './constants';
import { buildDownloadHeader } from './downloadTableView';
import type { DialogMainColumn } from './types';

/** Собирает срез выборки без постраничности (тот же путь, что и запрос списка). */
type BuildRequestParams = (params: {
  filters?: TableFilterState;
  sorting?: TableSortingState;
  dialogsPage?: SearchDialogsPage;
  splitPersonColumns?: boolean;
}) => Omit<SearchQueryRequest, 'limit' | 'offset'>;

/** Параметры выгрузки списка диалогов файлом */
type Params = {
  buildRequestParams: BuildRequestParams;
  /** Действующие фильтры выборки */
  filters: TableFilterState;
  /** Действующая сортировка выборки */
  sorting: TableSortingState;
  visibleAttributePaths: string[];
  visibleMainColumns: DialogMainColumn[];
  expertReportSearchParams: ExpertReportSettings[];
  /** Определения колонок таблицы деревом */
  columns: DataGridColumn<ConversationListItem>[];
  /** Эффективный порядок колонок таблицы */
  columnOrder: DataGridColumnOrderState;
  /** Закрепление колонок таблицы */
  columnPinning: DataGridColumnPinningState;
  /** Видимость колонок таблицы */
  columnVisibility: DataGridColumnVisibilityState;
  /** Ширина колонок таблицы */
  columnSizing: DataGridColumnSizingState;
  /** Направление текста заголовков; не задано — заголовки идут вдоль строки */
  headerOrientation?: TableHeaderOrientation;
  /** Название сохранённой выборки, по которой открыт список */
  presetName?: string;
  /** Высоты строк шапки таблицы на экране, сверху вниз */
  getHeaderRowHeights: () => number[] | undefined;
};

/** Выгрузка списка диалогов файлом */
type Result = {
  onDownloadList: () => void;
  isDownloadPending: boolean;
};

/**
 * Выгрузка списка диалогов файлом. Файл повторяет то, что пользователь видит в списке: тот же срез,
 * что уходит в загрузку строк, но без постраничности — выгружается вся выборка, а не показанная её
 * часть, — и тот же вид таблицы: колонки той же ширины, в том же порядке и под теми же
 * объединениями шапки, что и на экране.
 *
 * Колонки файла идут в том порядке, в каком стоят в таблице у пользователя: перестановка и
 * закрепление колонок доходят до файла. Списку такой порядок не нужен — колонки на экране
 * расставляет сам грид, — поэтому состав для выгрузки считается отдельно и запрос списка от
 * перестановки колонок не перезапрашивается.
 *
 * Роли участников уходят раздельно: в файле «Клиент» и «Консультант» — свои колонки, как и в
 * таблице, а разводить их из объединённого состава, как это делает список, некому.
 *
 * Колонки поиска по содержимому файл получает наравне с остальными — в том же начале таблицы, где
 * они стоят на экране; их состава файл не запрашивает, значения приносит сам режим поиска.
 */
export const useDialogDownload = ({
  buildRequestParams,
  filters,
  sorting,
  visibleAttributePaths,
  visibleMainColumns,
  expertReportSearchParams,
  columns,
  columnOrder,
  columnPinning,
  columnVisibility,
  columnSizing,
  headerOrientation,
  presetName,
  getHeaderRowHeights,
}: Params): Result => {
  const { t } = useTranslation('dialogs');
  const { mutate: downloadList, isPending: isDownloadPending } = useDownloadConversationsSearch();

  const downloadDialogsPage = useMemo(
    () => ({
      attributes: orderColumnIdsAsInTable(visibleAttributePaths.map(attributeColumnId), columnOrder, columnPinning).map(
        (columnId) => columnId.slice(ATTRIBUTE_COLUMN_PREFIX.length),
      ),
      mainColumns: orderColumnIdsAsInTable(visibleMainColumns, columnOrder, columnPinning) as DialogMainColumn[],
      expertReport: orderExpertReportSettingsAsInTable(expertReportSearchParams, columnOrder, columnPinning),
    }),
    [visibleAttributePaths, visibleMainColumns, expertReportSearchParams, columnOrder, columnPinning],
  );

  const onDownloadList = useCallback(() => {
    const params = buildRequestParams({
      filters,
      sorting,
      dialogsPage: downloadDialogsPage,
      splitPersonColumns: true,
    }) as Omit<SearchConversationDownloadRequest, 'entityType'>;

    const request = {
      ...params,
      view: {
        // Шапка собирается по уже готовому отбору файла: колонки поиска по содержимому доступны ей
        // ровно тогда, когда режим поиска ушёл и за строками, — иначе файл их не принял бы.
        header: buildDownloadHeader({
          columns,
          columnOrder,
          columnPinning,
          columnVisibility,
          columnSizing,
          dialogsPage: downloadDialogsPage,
          filters: params.filters,
        }),
        headerOrientation: headerOrientation ?? 'horizontal',
        headerRowHeights: getHeaderRowHeights(),
        presetName,
      },
      // Даты файла собирает бэкенд, а таблица показывает их во времени пользователя: без пояса
      // браузера он напечатал бы их в UTC.
      timezone: browserTimezone(),
    };

    downloadList(request, {
      // Отказ иначе прошёл бы молча: файла нет, а причины пользователь не видит. Она приходит
      // телом ответа, которое читалось файлом, поэтому разбирается здесь.
      onError: async (error) => {
        toastError(t('dialogListSection.downloadListError'), { description: await readApiErrorMessage(error) });
      },
    });
  }, [
    buildRequestParams,
    downloadList,
    t,
    filters,
    sorting,
    downloadDialogsPage,
    columns,
    columnOrder,
    columnPinning,
    columnVisibility,
    columnSizing,
    headerOrientation,
    presetName,
    getHeaderRowHeights,
  ]);

  return { onDownloadList, isDownloadPending };
};
